import { MultiTabDetection } from 'multi-tab-detection';
import { AccessTypes } from 'Utils/Access';
import VueRootStore from 'Stores/RootStore';
import FeatureToggleService from 'Services/FeatureToggleService';
import LoggerService from 'Services/LoggerService';

export const documentationTabsLsKey: string = 'mtd-documentation-tabs-opened';
export const backgroundTabsLsKey: string = 'mtd-background-tabs';

export class MultipleTabWarning {

    featureToggle: boolean = false;
    lsKey: string = 'mtd-number-of-tabs-opened';
    modalId: string  = 'multiple-tabs-modal';
    htmlCssClass: string  = 'multiple-tabs-open';
    loggerService: LoggerService = new LoggerService();
    multiTabDetection: MultiTabDetection = null;
    ignoreList: string[] = [
        '/Security/Login',
        'fullScreen=1'
    ];

    private logMessage = (messagePrefix: string) => {
        const userId = VueRootStore.state.user?.encUserId;
        const facilityId = VueRootStore.state.facility?.encFacilityId;
        // make sure VueRootStore is properly initialized and
        // values are being captured properly before logging them
        if (userId && facilityId) {
            const message = messagePrefix + ' for ' +
                '[FacilityId:' +  facilityId + '] ' +
                '[UserId:' + userId + '] ' +
                '[Date:' + (new Date()).toLocaleDateString() + ']';
            this.loggerService.postMessage({ message, error: false });
        }
    }

    // ----- Main Callback Function (everything tab-related calls this) ----- //

    private multiTabCallback = (numberOfTabsOpened: number, callbackType: string) => {

        if (typeof numberOfTabsOpened !== 'undefined') {
            // if a value exists, put it in localStorage
            localStorage.setItem(this.lsKey, numberOfTabsOpened.toString());
        } else {
            // this glitch usually triggers on EXISTING tab callback
            // if/when it does, pull the latest value from localStorage
            const lsNumber = parseInt(localStorage.getItem(this.lsKey), 10);
            if (!isNaN(lsNumber)) {
                numberOfTabsOpened = lsNumber;
            }
        }

        // if toggle is false, ignore this entirely
        if (this.featureToggle === false) {
            return;
        }

        // do not show the modal for users in the NhDev role
        if (VueRootStore.state.userRoles?.includes('NetHealthDevelopment')) {
            return;
        }

        VueRootStore.commit('setTabsOpenedTotal', numberOfTabsOpened);
        if (callbackType === 'NEW') {
            this.logMessage('Multiple Tab Warning');
        }
    };

    // ----- Individial Callback Subscriptions ----- //

    public init = () => {

        // suppress logic on Android/Apple devices
        const ua = navigator.userAgent;
        const android: boolean = /android/i.test(ua);
        const ipadLegacy: boolean = /iPad|iPhone|iPod/.test(ua);
        const ipadChrome: boolean = /CriOS/.test(ua);
        const ipadSafari: boolean = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        if (android || ipadLegacy || ipadChrome || ipadSafari) {
            return;
        }

        // "blacklist" certain URLs that we expect to open in seperate tabs.
        const ignoreListMatches = this.ignoreList.filter(x => window.location.href.indexOf(x) !== -1);
        if (ignoreListMatches.length > 0) {
            return;
        }

        this.multiTabDetection = new MultiTabDetection();

        this.multiTabDetection.NewTabDetectedEvent.subscribe({
            next: (numberOfTabsOpened: number) => {
                this.multiTabCallback(numberOfTabsOpened, 'NEW');
            }
        });

        this.multiTabDetection.ClosedTabDetectedEvent.subscribe({
            next: (numberOfTabsOpened: number) => {
                this.multiTabCallback(numberOfTabsOpened, 'CLOSED');
            }
        });

        this.multiTabDetection.ExistingTabDetectedEvent.subscribe({
            next: (numberOfTabsOpened: number) => {
                this.multiTabCallback(numberOfTabsOpened, 'EXISTING');
            }
        });

        FeatureToggleService.checkFeatureToggle(AccessTypes.FeatureToggles.MultipleTabsWarningModal, false).then((result: boolean) => {
            // set the scoped variable, then make sure
            // multi-tab logic triggers (after variable is set)
            this.featureToggle = result;
            this.multiTabCallback(undefined, 'EXISTING');
        });

        // only fires when setItem is triggered by a DIFFERENT tab
        window.addEventListener('storage', (ev: StorageEvent) => {
            if (ev.key === documentationTabsLsKey) {
                try {
                    const ls: number = parseInt(ev.newValue, 10);
                    const val: number = !isNaN(ls) ? ls : 0;
                    VueRootStore.commit('setTabsOpenedDocumentation', val);
                } catch(e) { }
            } else if (ev.key === backgroundTabsLsKey && this.featureToggle === true) {
                // all localStorage keys are string values
                if (ev.newValue !== 'null') {
                    this.logMessage('Background Tab ' + ev.newValue);
                }
            }
        }, false);

        // decrement tab count if the user closes a tab with a workflow URL
        window.addEventListener('beforeunload', (ev: BeforeUnloadEvent) => {
            if(window.location.href.indexOf('/#/workflow') !== -1) {
                VueRootStore.dispatch('alterDocumentationTabCount', false);
            }
        }, false);
    }
}